import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  Download, FileText, Building2, CheckCircle2, AlertTriangle, Clock,
  Plus, RefreshCw, Eye, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function PRSExportPage() {
  const [registrations, setRegistrations] = useState([]);
  const [exportLogs, setExportLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    landlord_name: '',
    landlord_email: '',
    landlord_phone: '',
    landlord_address: '',
    is_company: false,
    company_name: '',
    company_number: ''
  });

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const response = await api.get('/prs-services/prs/registrations');
      setRegistrations(response.data.registrations);
    } catch (error) {
      toast.error('Failed to load PRS registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchExportLogs = async (registrationId) => {
    try {
      const response = await api.get(`/prs-services/prs/exports/${registrationId}/logs`);
      setExportLogs(response.data.logs);
    } catch (error) {
      toast.error('Failed to load export logs');
    }
  };

  const handleCreateRegistration = async (e) => {
    e.preventDefault();
    try {
      await api.post('/prs-services/prs/registrations', formData);
      toast.success('PRS registration created successfully');
      setShowCreateForm(false);
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to create registration');
    }
  };

  const handleGenerateExport = async (registrationId, format = 'csv') => {
    try {
      const response = await api.post('/prs-services/prs/exports', {
        registration_id: registrationId,
        export_type: 'full',
        export_format: format
      });
      
      if (response.data.data) {
        // Download the file
        const blob = new Blob([response.data.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `prs_export_${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
      }
      
      toast.success(`Export generated: ${response.data.properties_exported} properties`);
      if (selectedRegistration) {
        fetchExportLogs(selectedRegistration);
      }
    } catch (error) {
      toast.error('Failed to generate export');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      registered: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="prs-export-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">PRS Database Export</h1>
            <p className="text-slate-600">Register properties and generate government reports</p>
          </div>
          <Button onClick={() => setShowCreateForm(true)} data-testid="create-registration-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Registration
          </Button>
        </div>

        {showCreateForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create PRS Registration</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRegistration} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Landlord Name"
                    value={formData.landlord_name}
                    onChange={(e) => setFormData({...formData, landlord_name: e.target.value})}
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={formData.landlord_email}
                    onChange={(e) => setFormData({...formData, landlord_email: e.target.value})}
                    required
                  />
                  <Input
                    placeholder="Phone"
                    value={formData.landlord_phone}
                    onChange={(e) => setFormData({...formData, landlord_phone: e.target.value})}
                  />
                  <Input
                    placeholder="Address"
                    value={formData.landlord_address}
                    onChange={(e) => setFormData({...formData, landlord_address: e.target.value})}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_company}
                      onChange={(e) => setFormData({...formData, is_company: e.target.checked})}
                    />
                    <span>Company Registration</span>
                  </label>
                </div>
                {formData.is_company && (
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Company Name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    />
                    <Input
                      placeholder="Company Number"
                      value={formData.company_number}
                      onChange={(e) => setFormData({...formData, company_number: e.target.value})}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit">Create Registration</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  PRS Registrations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : registrations.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No PRS registrations yet. Create one to get started.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {registrations.map((reg) => (
                      <div
                        key={reg.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedRegistration === reg.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => {
                          setSelectedRegistration(reg.id);
                          fetchExportLogs(reg.id);
                        }}
                        data-testid={`registration-${reg.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{reg.landlord_name}</h3>
                            <p className="text-sm text-slate-500">{reg.landlord_email}</p>
                            {reg.company_name && (
                              <p className="text-sm text-slate-500">{reg.company_name}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {getStatusBadge(reg.status)}
                            <p className="text-sm text-slate-500 mt-1">
                              {reg.total_properties} properties
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); handleGenerateExport(reg.id, 'csv'); }}>
                            <Download className="h-3 w-3 mr-1" />
                            CSV
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleGenerateExport(reg.id, 'json'); }}>
                            <Download className="h-3 w-3 mr-1" />
                            JSON
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Export History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedRegistration ? (
                  <p className="text-slate-500 text-sm">Select a registration to view export history</p>
                ) : exportLogs.length === 0 ? (
                  <p className="text-slate-500 text-sm">No exports yet</p>
                ) : (
                  <div className="space-y-3">
                    {exportLogs.map((log) => (
                      <div key={log.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{log.export_format.toUpperCase()}</span>
                          <Badge variant={log.status === 'completed' ? 'success' : 'warning'}>
                            {log.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(log.started_at).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {log.properties_exported} properties exported
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
